// src/redux/dynamicField/dynamicFieldThunks.js
import { createAsyncThunk } from "@reduxjs/toolkit";
import {
  fetchDynamicFields,
  getDynamicFieldById,
  createDynamicField,
  updateDynamicField,
  deleteDynamicField,
  bulkDeleteDynamicFields,
  changeDynamicFieldStatus,
} from "../../components/services/dynamicFieldService";
import { handleUnauthorized } from "../../utils/apiHelper";

// ✅ Get all dynamic fields (with filters)
export const fetchAllDynamicFields = createAsyncThunk(
  "dynamicField/fetchAll",
  async ({ params, navigate }, { rejectWithValue }) => {
    try {
      const { data } = await fetchDynamicFields(params);
      return data;
    } catch (err) {
      if (err.response?.status === 401) handleUnauthorized(navigate);
      return rejectWithValue(err.message || "Failed to fetch fields");
    }
  }
);

// ✅ Get field by ID
export const fetchDynamicFieldById = createAsyncThunk(
  "dynamicField/fetchById",
  async ({ id, navigate }, { rejectWithValue }) => {
    try {
      const { data } = await getDynamicFieldById(id);
      return data.data;
    } catch (err) {
      if (err.response?.status === 401) handleUnauthorized(navigate);
      return rejectWithValue(err.message || "Failed to fetch field");
    }
  }
);

// ✅ Create new field
export const createDynamicFieldById = createAsyncThunk(
  "dynamicField/create",
  async ({ data, navigate }, { rejectWithValue }) => {
    try {
      const res = await createDynamicField(data);
      return res.data.data;
    } catch (err) {
      if (err.response?.status === 401) handleUnauthorized(navigate);
      // Forward backend error if available
      if (err.response?.data) {
        return rejectWithValue(err.response.data);
      }
      return rejectWithValue(err.message || "Failed to create field");
    }
  }
);

// ✅ Update field
export const updateDynamicFieldById = createAsyncThunk(
  "dynamicField/update",
  async ({ id, data, navigate }, { rejectWithValue }) => {
    try {
      const res = await updateDynamicField(id, data);
      return res.data.data;
    } catch (err) {
      if (err.response?.status === 401) handleUnauthorized(navigate);
      if (err.response?.data) {
        return rejectWithValue(err.response.data);
      }
      return rejectWithValue(err.message || "Failed to update field");
    }
  }
);

// ✅ Change status
export const toggleDynamicFieldStatus = createAsyncThunk(
  "dynamicField/changeStatus",
  async ({ id, data, navigate }, { rejectWithValue }) => {
    try {
      const res = await changeDynamicFieldStatus(id, data);
      return { id, isActive: res.data.data.isActive };
    } catch (err) {
      if (err.response?.status === 401) handleUnauthorized(navigate);
      return rejectWithValue(err.message || "Failed to change status");
    }
  }
);

// ✅ Delete field
export const deleteDynamicFieldById = createAsyncThunk(
  "dynamicField/delete",
  async ({ id, navigate }, { rejectWithValue }) => {
    try {
      await deleteDynamicField(id);
      return id;
    } catch (err) {
      if (err.response?.status === 401) handleUnauthorized(navigate);
      return rejectWithValue(err.message || "Failed to delete field");
    }
  }
);

// ✅ Bulk delete
export const deleteDynamicFieldAlls = createAsyncThunk(
  "dynamicField/deleteAll",
  async ({ ids, navigate }, { rejectWithValue }) => {
    try {
      const res = await bulkDeleteDynamicFields(ids);
      return { ids, ...res.data };
    } catch (err) {
      if (err.response?.status === 401) handleUnauthorized(navigate);
      return rejectWithValue(err.message || "Failed to delete fields");
    }
  }
);
